/**
 * La captura del sharer: pantalla (getDisplayMedia) + voz (micrófono) + audio del sistema cuando
 * el navegador lo da, mezclados en UN solo track de audio por `mixer.ts` (spec §4.4 y §4.10).
 *
 * REGLA DE ORO: `getDisplayMedia` se llama SÍNCRONAMENTE dentro del clic (activación transitoria).
 * Cualquier `await` antes la pierde y el navegador contesta NotAllowedError sin picker. Por eso
 * `captureScreen()` arma la promesa del picker Y el `AudioContext` ANTES de su primer await, y recién
 * después pide el micrófono (mismo gesto: el prompt del mic sale al cerrarse el picker).
 *
 * Audio del sistema por navegador (decisión del dueño 2026-09-03): Chromium/Windows lo da para la
 * pantalla completa y la pestaña; Chromium/macOS solo para la pestaña (sistema desde Chrome 141 +
 * macOS 14.2); Safari y Firefox nunca. Se pide `audio: true` siempre y se mira qué vino; el chip del
 * sharer explica lo que falta (`systemAudio`). El mic se pide aparte con cancelación de eco y supresión
 * de ruido; si lo niegan se comparte sin voz y se avisa.
 *
 * Firefox: las opciones del picker (`selfBrowserSurface`, `surfaceSwitching`, `systemAudio`…) son
 * miembros de diccionario que ignora solo; lo que SÍ puede romper son las constraints de video que no
 * conoce, por eso `displayConstraints` mira `getSupportedConstraints` antes de pedir frameRate/width.
 */
import { buildMixer, systemAudioSupported, type Mixer, type MixPlan } from './mixer';

export type CaptureSurface = 'monitor' | 'window' | 'browser';
export type CaptureError = 'cancelled' | 'mac_permission' | 'unsupported' | 'failed';
/** Qué pasó con el audio del sistema: vino, no vino pudiendo (ventana/pestaña sin audio en Chromium), o el navegador no lo da. */
export type SystemAudioState = 'included' | 'notIncluded' | 'unavailable';

export type Capture = {
  /** Lo que va al peer: video + (si hay) el track de audio mezclado. */
  stream: MediaStream;
  videoTrack: MediaStreamTrack;
  surface: CaptureSurface | null;
  hasSystemAudio: boolean;
  systemAudio: SystemAudioState;
  mic: 'on' | 'denied' | 'none';
  /** Cómo se armó el audio (mezcla / directo / nada). Va a `stats_json` como `audio_plan`. */
  audioPlan: MixPlan;
  /** Silenciar la voz (track del mic en `enabled=false` + ganancia 0): el audio del sistema sigue. */
  setMuted(muted: boolean): void;
  isMuted(): boolean;
  /**
   * ¿El audio SALIENTE está mudo por un AudioContext de la mezcla suspendido? (para la red de
   * seguridad del provider: si es true, el track existe pero no lleva señal — hay que reanudar).
   */
  isAudioSuspended(): boolean;
  /** Reanuda el AudioContext de la mezcla desde un gesto; true si el saliente vuelve a llevar señal. */
  resumeAudio(): Promise<boolean>;
  /** La barra nativa "Stop sharing" o el cierre por nuestra cuenta. */
  onEnded(cb: () => void): void;
  stop(): void;
};

export class CaptureFailed extends Error {
  kind: CaptureError;
  constructor(kind: CaptureError, message?: string) {
    super(message ?? kind);
    this.kind = kind;
  }
}

export function displayMediaSupported(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices && 'getDisplayMedia' in navigator.mediaDevices;
}

function classify(e: unknown): CaptureFailed {
  const err = e as { name?: string; message?: string };
  if (err?.name === 'NotAllowedError') {
    // Chrome/Edge/Opera en macOS sin "Screen & System Audio Recording": el mensaje trae "system".
    if (/system/i.test(err.message ?? '')) return new CaptureFailed('mac_permission', err.message);
    return new CaptureFailed('cancelled', err.message);
  }
  if (err?.name === 'NotSupportedError' || err?.name === 'TypeError') return new CaptureFailed('unsupported', err.message);
  if (err?.name === 'AbortError') return new CaptureFailed('cancelled', err.message);
  return new CaptureFailed('failed', err?.message);
}

/**
 * Las opciones de `getDisplayMedia` (spec §4.4). Pura, para probarla: `supported` es lo que devuelve
 * `navigator.mediaDevices.getSupportedConstraints()` (null = API ausente → se asume Chromium). Si el
 * navegador no declara frameRate/width/height (Firefox viejo), el video se pide como `true` y listo:
 * una constraint desconocida en `video` puede tirar OverconstrainedError sin abrir el picker.
 * `selfBrowser` = 'include' es para Record (grabarse a uno mismo); el share en vivo se excluye.
 */
export function displayConstraints(
  supported: Partial<Record<string, boolean>> | null | undefined,
  opts: { selfBrowser?: 'include' | 'exclude'; systemAudio?: boolean } = {},
): DisplayMediaStreamOptions {
  const known = (k: string) => supported == null || supported[k] === true;
  const video = known('frameRate') && known('width') && known('height')
    ? { frameRate: { ideal: 30, max: 30 }, width: { max: 1920 }, height: { max: 1080 } }
    : true;
  return {
    video,
    // Record deja apagar el audio del sistema desde su modal (`systemAudio: false`); el share en
    // vivo lo pide siempre.
    audio: opts.systemAudio !== false,
    // Chromium: ofrecer el audio del sistema en el picker; sin el chime de nuestra propia pestaña
    // (`restrictOwnAudio`, Chrome 141+); sin la pestaña del app en la lista; se puede cambiar de
    // superficie sin cortar; los monitores completos sí se ofrecen. Firefox/Safari los ignoran.
    systemAudio: 'include',
    restrictOwnAudio: true,
    selfBrowserSurface: opts.selfBrowser ?? 'exclude',
    surfaceSwitching: 'include',
    monitorTypeSurfaces: 'include',
  } as unknown as DisplayMediaStreamOptions;
}

function supportedConstraints(): Partial<Record<string, boolean>> | null {
  try {
    const md = navigator.mediaDevices as MediaDevices & { getSupportedConstraints?: () => MediaTrackSupportedConstraints };
    return typeof md.getSupportedConstraints === 'function' ? (md.getSupportedConstraints() as unknown as Partial<Record<string, boolean>>) : null;
  } catch { return null; }
}

export type CaptureOpts = {
  withMic: boolean;
  /** Record: el micrófono elegido en el modal (`deviceId` exacto; si falla se cae al default). */
  micDeviceId?: string;
  /** Record se incluye a sí mismo en el picker (grabarse explicando el app); el share en vivo no. */
  selfBrowser?: 'include' | 'exclude';
  /** false = no pedir audio del sistema/pestaña (toggle del modal de Record). Default true. */
  withSystemAudio?: boolean;
};

/**
 * Debe llamarse en el mismo tick del clic. Devuelve la captura lista para `SharePeer.attach` (o
 * para el `MediaRecorder` de Record). `withMic` en false salta el micrófono (el audio del sistema
 * se intenta igual).
 */
export function captureScreen(opts: CaptureOpts = { withMic: true }): Promise<Capture> {
  if (!displayMediaSupported()) return Promise.reject(new CaptureFailed('unsupported'));
  const constraints = displayConstraints(supportedConstraints(), { selfBrowser: opts.selfBrowser, systemAudio: opts.withSystemAudio });
  // El AudioContext nace DENTRO del gesto (spec §4.10): creado después de un await puede quedar
  // `suspended` y el viewer oiría silencio sin ningún error a la vista. Si al final no hace falta
  // (mic directo / sin audio), el mixer lo cierra.
  let ctx: AudioContext | null = null;
  try { ctx = typeof AudioContext !== 'undefined' ? new AudioContext() : null; } catch { ctx = null; }
  // SÍNCRONO: la promesa nace dentro de la activación del usuario.
  const pickerPromise = navigator.mediaDevices.getDisplayMedia(constraints);
  return finishCapture(pickerPromise, opts.withMic, ctx, opts.micDeviceId);
}

/** Constraints del micrófono: cancelación de eco y supresión de ruido, más el dispositivo elegido. */
export function micConstraints(deviceId?: string): MediaTrackConstraints {
  const base: MediaTrackConstraints = { echoCancellation: true, noiseSuppression: true, autoGainControl: true };
  return deviceId ? { ...base, deviceId: { exact: deviceId } } : base;
}

async function finishCapture(pickerPromise: Promise<MediaStream>, withMic: boolean, ctx: AudioContext | null, micDeviceId?: string): Promise<Capture> {
  const closeCtx = () => { if (ctx) { void ctx.close().catch(() => {}); ctx = null; } };
  let display: MediaStream;
  try { display = await pickerPromise; }
  catch (e) { closeCtx(); throw classify(e); }

  const videoTrack = display.getVideoTracks()[0];
  if (!videoTrack) {
    for (const t of display.getTracks()) t.stop();
    closeCtx();
    throw new CaptureFailed('failed', 'no video track');
  }
  const settings = videoTrack.getSettings() as MediaTrackSettings & { displaySurface?: string };
  const surface = (['monitor', 'window', 'browser'] as const).find((s) => s === settings.displaySurface) ?? null;
  const systemTrack = display.getAudioTracks()[0] ?? null;
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const systemAudio: SystemAudioState = systemTrack ? 'included' : systemAudioSupported(ua) ? 'notIncluded' : 'unavailable';

  // El micrófono, aparte y en el mismo gesto: negarlo no cancela la pantalla.
  let micStream: MediaStream | null = null;
  let mic: Capture['mic'] = 'none';
  if (withMic && navigator.mediaDevices?.getUserMedia) {
    try {
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints(micDeviceId) });
      } catch (e) {
        // El dispositivo elegido ya no está (se desenchufó el auricular): se cae al default en vez
        // de grabar sin voz. Cualquier otro error se trata abajo.
        if (micDeviceId && (e as { name?: string })?.name === 'OverconstrainedError') micStream = await navigator.mediaDevices.getUserMedia({ audio: micConstraints() });
        else throw e;
      }
      mic = micStream.getAudioTracks().length ? 'on' : 'none';
    } catch (e) {
      // NotFoundError = no hay dispositivo (headless, PC sin mic); lo demás es un permiso negado.
      mic = (e as { name?: string })?.name === 'NotFoundError' ? 'none' : 'denied';
    }
  }

  // Un solo track de audio al peer (o ninguno). El mixer decide mezcla vs directo, ESPERA el resume
  // del contexto (ya no fire-and-forget) y cierra el AudioContext si no lo usa.
  const mixer: Mixer = await buildMixer(ctx, micStream, systemTrack, ua);
  ctx = null; // ya es del mixer
  const tracks: MediaStreamTrack[] = [videoTrack];
  if (mixer.track) tracks.push(mixer.track);
  const stream = new MediaStream(tracks);

  const endedCbs: Array<() => void> = [];
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    for (const t of display.getTracks()) { try { t.stop(); } catch { /* */ } }
    for (const t of micStream?.getTracks() ?? []) { try { t.stop(); } catch { /* */ } }
    mixer.close();
  };
  videoTrack.addEventListener('ended', () => {
    // Barra nativa "Stop sharing": se avisa a quien escucha y se limpia todo.
    for (const cb of endedCbs) { try { cb(); } catch { /* */ } }
    stop();
  });

  return {
    stream,
    videoTrack,
    surface,
    hasSystemAudio: !!systemTrack,
    systemAudio,
    mic,
    audioPlan: mixer.plan,
    setMuted: (m: boolean) => mixer.setMuted(m),
    isMuted: () => mixer.isMuted(),
    isAudioSuspended: () => mixer.isSuspended(),
    resumeAudio: () => mixer.resume(),
    onEnded(cb) { endedCbs.push(cb); },
    stop,
  };
}
